"use strict";

Object.defineProperty(exports, "__esModule", {
  value: true
});
exports["default"] = void 0;

var _asserts = require("../asserts.js");

/**
 * @module ol/structs/LRUCache
 */

/**
 * @typedef {Object} Entry
 * @property {string} key_ Key.
 * @property {Object} newer Newer.
 * @property {Object} older Older.
 * @property {*} value_ Value.
 */

/**
 * @classdesc
 * Implements a Least-Recently-Used cache where the keys do not conflict with
 * Object's properties (e.g. 'hasOwnProperty' is not allowed as a key). Expiring
 * items from the cache is the responsibility of the user.
 *
 * @fires import("../events/Event.js").default
 * @template T
 */
var LRUCache =
/** @class */
function () {
  /**
   * @param {number} [opt_highWaterMark] High water mark.
   */
  function LRUCache(opt_highWaterMark) {
    /**
     * Desired max cache size after expireCache(). If set to 0, no cache entries
     * will be pruned at all.
     * @type {number}
     */
    this.highWaterMark = opt_highWaterMark !== undefined ? opt_highWaterMark : 2048;
    /**
     * @private
     * @type {number}
     */

    this.count_ = 0;
    /**
     * @private
     * @type {!Object<string, Entry>}
     */

    this.entries_ = {};
    /**
     * @private
     * @type {?Entry}
     */

    this.oldest_ = null;
    /**
     * @private
     * @type {?Entry}
     */

    this.newest_ = null;
  }
  /**
   * @return {boolean} Can expire cache.
   */


  LRUCache.prototype.canExpireCache = function () {
    return this.highWaterMark > 0 && this.getCount() > this.highWaterMark;
  };
  /**
   * Expire the cache.
   * @param {!Object<string, boolean>} [keep] Keys to keep. To be implemented by subclasses.
   */


  LRUCache.prototype.expireCache = function (keep) {
    while (this.canExpireCache()) {
      this.pop();
    }
  };
  /**
   * FIXME empty description for jsdoc
   */


  LRUCache.prototype.clear = function () {
    this.count_ = 0;
    this.entries_ = {};
    this.oldest_ = null;
    this.newest_ = null;
  };
  /**
   * @param {string} key Key.
   * @return {boolean} Contains key.
   */


  LRUCache.prototype.containsKey = function (key) {
    return this.entries_.hasOwnProperty(key);
  };
  /**
   * @param {function(T, string, LRUCache<T>): ?} f The function
   *     to call for every entry from the oldest to the newer. This function takes
   *     3 arguments (the entry value, the entry key and the LRUCache object).
   *     The return value is ignored.
   */


  LRUCache.prototype.forEach = function (f) {
    var entry = this.oldest_;

    while (entry) {
      f(entry.value_, entry.key_, this);
      entry = entry.newer;
    }
  };
  /**
   * @param {string} key Key.
   * @param {*} [opt_options] Options (reserved for subclasses).
   * @return {T} Value.
   */


  LRUCache.prototype.get = function (key, opt_options) {
    var entry = this.entries_[key];
    (0, _asserts.assert)(entry !== undefined, 15); // Tried to get a value for a key that does not exist in the cache

    if (entry === this.newest_) {
      return entry.value_;
    } else if (entry === this.oldest_) {
      this.oldest_ =
      /** @type {Entry} */
      this.oldest_.newer;
      this.oldest_.older = null;
    } else {
      entry.newer.older = entry.older;
      entry.older.newer = entry.newer;
    }

    entry.newer = null;
    entry.older = this.newest_;
    this.newest_.newer = entry;
    this.newest_ = entry;
    return entry.value_;
  };
  /**
   * Remove an entry from the cache.
   * @param {string} key The entry key.
   * @return {T} The removed entry.
   */


  LRUCache.prototype.remove = function (key) {
    var entry = this.entries_[key];
    (0, _asserts.assert)(entry !== undefined, 15); // Tried to get a value for a key that does not exist in the cache

    if (entry === this.newest_) {
      this.newest_ =
      /** @type {Entry} */
      entry.older;

      if (this.newest_) {
        this.newest_.newer = null;
      }
    } else if (entry === this.oldest_) {
      this.oldest_ =
      /** @type {Entry} */
      entry.newer;

      if (this.oldest_) {
        this.oldest_.older = null;
      }
    } else {
      entry.newer.older = entry.older;
      entry.older.newer = entry.newer;
    }

    delete this.entries_[key];
    --this.count_;
    return entry.value_;
  };
  /**
   * @return {number} Count.
   */


  LRUCache.prototype.getCount = function () {
    return this.count_;
  };
  /**
   * @return {Array<string>} Keys.
   */


  LRUCache.prototype.getKeys = function () {
    var keys = new Array(this.count_);
    var i = 0;
    var entry;

    for (entry = this.newest_; entry; entry = entry.older) {
      keys[i++] = entry.key_;
    }

    return keys;
  };
  /**
   * @return {Array<T>} Values.
   */


  LRUCache.prototype.getValues = function () {
    var values = new Array(this.count_);
    var i = 0;
    var entry;

    for (entry = this.newest_; entry; entry = entry.older) {
      values[i++] = entry.value_;
    }

    return values;
  };
  /**
   * @return {T} Last value.
   */


  LRUCache.prototype.peekLast = function () {
    return this.oldest_.value_;
  };
  /**
   * @return {string} Last key.
   */


  LRUCache.prototype.peekLastKey = function () {
    return this.oldest_.key_;
  };
  /**
   * Get the key of the newest item in the cache.  Throws if the cache is empty.
   * @return {string} The newest key.
   */


  LRUCache.prototype.peekFirstKey = function () {
    return this.newest_.key_;
  };
  /**
   * @return {T} value Value.
   */


  LRUCache.prototype.pop = function () {
    var entry = this.oldest_;
    delete this.entries_[entry.key_];

    if (entry.newer) {
      entry.newer.older = null;
    }

    this.oldest_ =
    /** @type {Entry} */
    entry.newer;

    if (!this.oldest_) {
      this.newest_ = null;
    }

    --this.count_;
    return entry.value_;
  };
  /**
   * @param {string} key Key.
   * @param {T} value Value.
   */


  LRUCache.prototype.replace = function (key, value) {
    this.get(key); // update `newest_`

    this.entries_[key].value_ = value;
  };
  /**
   * @param {string} key Key.
   * @param {T} value Value.
   */


  LRUCache.prototype.set = function (key, value) {
    (0, _asserts.assert)(!(key in this.entries_), 16); // Tried to set a value for a key that is used already

    var entry = {
      key_: key,
      newer: null,
      older: this.newest_,
      value_: value
    };

    if (!this.newest_) {
      this.oldest_ = entry;
    } else {
      this.newest_.newer = entry;
    }

    this.newest_ = entry;
    this.entries_[key] = entry;
    ++this.count_;
  };
  /**
   * Set a maximum number of entries for the cache.
   * @param {number} size Cache size.
   * @api
   */


  LRUCache.prototype.setSize = function (size) {
    this.highWaterMark = size;
  };

  return LRUCache;
}();

var _default = LRUCache;
exports["default"] = _default;
//# sourceMappingURL=data:application/json;charset=utf-8;base64,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